import React from 'react';
import { Save, Edit3 } from 'lucide-react';

import EditableInput from './common/EditableInput';
import AffiliateCard from './common/AffiliateCard';
import { AFFILIATE_LINKS, ARC_PRODUCTS } from '../config/affiliateLinks';

/**
 * NetWorthTab – assets, liabilities, net-worth & FIRE goal progress with auto-calculations.
 *
 * Props
 *  ─ netWorth:      { assets, liabilities, netWorth, fireGoal, progress }
 *  ─ editMode:      boolean
 *  ─ onToggleEdit:  () => void
 *  ─ updateNetWorth(field, value): parent updater
 *  ─ totalInvestments: number - auto-calculated from investments
 *  ─ totalDebts: number - auto-calculated from debts
 */
const NetWorthTab = ({
  netWorth,
  editMode,
  onToggleEdit,
  updateNetWorth,
  totalInvestments = 0,
  totalDebts = 0,
  wallets = []
}) => {
  // On-chain crypto holdings = sum of wallet effective values
  const totalCryptoHoldings = wallets.reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );

  // Auto-calculated values — Total Assets now includes wallets
  const autoAssets = totalInvestments + totalCryptoHoldings;
  const autoLiabilities = totalDebts;
  const autoNetWorth = autoAssets - autoLiabilities;
  const autoProgress = netWorth.fireGoal ? (autoNetWorth / netWorth.fireGoal) * 100 : 0;

  return (
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Net Worth Analysis</h3>
        <button
          onClick={onToggleEdit}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            editMode ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
          } hover:opacity-90`}
        >
          {editMode ? <Save size={16} /> : <Edit3 size={16} />} {editMode ? 'Save' : 'Edit'}
        </button>
      </div>

      {/* Auto-calculation info panel */}
      <div className="mb-6 p-4 rounded-2xl border border-arc-line bg-white/[0.02]">
        <p className="eyebrow mb-3">Auto-Calculated Net Worth</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-arc-muted text-xs">Manual Investments</p>
            <p className="font-semibold num text-arc-ink">
              ${totalInvestments.toLocaleString()}
            </p>
            <p className="text-[10px] text-arc-dim mt-0.5">From Investments tab</p>
          </div>
          <div>
            <p className="text-arc-muted text-xs">On-Chain Wallets</p>
            <p className="font-semibold num text-arc-gold">
              ${totalCryptoHoldings.toLocaleString()}
            </p>
            <p className="text-[10px] text-arc-dim mt-0.5">From Wallets tab</p>
          </div>
          <div>
            <p className="text-arc-muted text-xs">Total Liabilities</p>
            <p className="font-semibold num text-red-400">
              ${autoLiabilities.toLocaleString()}
            </p>
            <p className="text-[10px] text-arc-dim mt-0.5">From Debt tab</p>
          </div>
          <div>
            <p className="text-arc-muted text-xs">Net Worth</p>
            <p className={`font-semibold text-lg num ${autoNetWorth >= 0 ? 'text-arc-light' : 'text-red-400'}`}>
              ${autoNetWorth.toLocaleString()}
            </p>
            <p className="text-[10px] text-arc-dim mt-0.5">Assets − Liabilities</p>
          </div>
        </div>
      </div>

      {/* Asset / Liability / NW cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {[
          {
            label: 'Total Assets',
            value: autoAssets,
            bg: 'bg-green-50',
            color: 'text-green-600',
            autoCalculated: true,
            description: 'Sum of all investments'
          },
          {
            label: 'Total Liabilities',
            value: autoLiabilities,
            bg: 'bg-red-50',
            color: 'text-red-600',
            autoCalculated: true,
            description: 'Sum of all current debts'
          },
          {
            label: 'Net Worth',
            value: autoNetWorth,
            bg: autoNetWorth >= 0 ? 'bg-blue-50' : 'bg-orange-50',
            color: autoNetWorth >= 0 ? 'text-blue-600' : 'text-orange-600',
            autoCalculated: true,
            description: 'Assets - Liabilities'
          },
        ].map(({ label, value, bg, color, autoCalculated, description }) => (
          <div key={label} className={`text-center p-4 rounded-lg ${bg} border`}>
            <p className="text-arc-muted text-sm">{label}</p>
            <p className={`font-bold ${color} ${label === 'Net Worth' ? 'text-3xl' : 'text-2xl'}`}>
              ${value.toLocaleString()}
            </p>
            {autoCalculated && (
              <>
                <p className="text-xs text-arc-dim mt-1">{description}</p>
                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs mt-1">
                  Auto-calculated
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* FIRE goal progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium">FIRE Goal Progress</span>
          <span className="text-lg font-semibold">{autoProgress.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-white/[0.08] rounded-full h-4">
          <div
            className={`h-4 rounded-full transition-all duration-300 ${
              autoProgress >= 100 ? 'bg-green-600' : 'bg-blue-600'
            }`}
            style={{ width: `${Math.min(autoProgress, 100)}%` }}
          />
        </div>
        <div className="flex justify-between items-center mt-2 text-sm">
          <span className="text-arc-muted">
            Current: ${autoNetWorth.toLocaleString()}
          </span>
          <span className="text-arc-muted">
            Target: {editMode ? (
              <EditableInput
                value={netWorth.fireGoal || 0}
                onChange={(v) => updateNetWorth('fireGoal', v)}
                className="w-24 ml-1 text-sm"
              />
            ) : (
              `$${(netWorth.fireGoal || 0).toLocaleString()}`
            )}
          </span>
        </div>
        <div className="mt-3 text-center">
          {autoProgress >= 100 ? (
            <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
              <p className="text-green-800 font-medium">
                🎉 Congratulations! You've achieved your FIRE goal!
              </p>
            </div>
          ) : (
            <p className="text-arc-muted">
              ${((netWorth.fireGoal || 0) - autoNetWorth).toLocaleString()} remaining to reach FIRE goal
            </p>
          )}
        </div>
      </div>

      {/* Net Worth Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Assets Breakdown */}
        <div className="p-4 bg-green-50 rounded-lg">
          <h4 className="font-semibold text-green-800 mb-3">Assets Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Investment Portfolio:</span>
              <span className="font-semibold">${totalInvestments.toLocaleString()}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total Assets:</span>
              <span className="text-green-600">${autoAssets.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-arc-dim mt-2">
            💡 Add more investments in the Investments tab to increase your assets
          </p>
        </div>

        {/* Liabilities Breakdown */}
        <div className="p-4 bg-red-50 rounded-lg">
          <h4 className="font-semibold text-red-800 mb-3">Liabilities Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Outstanding Debts:</span>
              <span className="font-semibold">${totalDebts.toLocaleString()}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total Liabilities:</span>
              <span className="text-red-600">${autoLiabilities.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-arc-dim mt-2">
            💡 Pay down debts in the Debt tab to reduce your liabilities
          </p>
        </div>
      </div>

      {/* Net Worth Trend Indicator */}
      <div className="mt-6 p-4 bg-white/[0.03] rounded-lg">
        <h4 className="font-semibold text-arc-ink mb-2">Net Worth Status</h4>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-full ${
            autoNetWorth >= 1000000 ? 'bg-green-100 text-green-800' :
            autoNetWorth >= 500000 ? 'bg-blue-100 text-blue-800' :
            autoNetWorth >= 100000 ? 'bg-yellow-100 text-yellow-800' :
            autoNetWorth >= 0 ? 'bg-white/[0.05] text-arc-ink' :
            'bg-red-100 text-red-800'
          }`}>
            <span className="font-medium">
              {autoNetWorth >= 1000000 ? '🚀 Millionaire!' :
               autoNetWorth >= 500000 ? '💪 Half Millionaire' :
               autoNetWorth >= 100000 ? '📈 Six Figures' :
               autoNetWorth >= 0 ? '⚖️ Positive Net Worth' :
               '⚠️ Negative Net Worth'}
            </span>
          </div>
          <span className="text-sm text-arc-muted">
            {autoNetWorth >= 0 ? 'Keep building wealth!' : 'Focus on debt reduction'}
          </span>
        </div>
      </div>

      {/* Contextual recommendation: EtherFi for putting ETH to work.
          Net Worth tab is a natural place — user is looking at idle assets
          and asking "how do I make this work harder?" */}
      {(() => {
        const etherfi = AFFILIATE_LINKS.find((l) => l.id === "etherfi");
        if (!etherfi) return null;
        return (
          <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
            <p className="eyebrow mb-1">Earn yield on idle ETH</p>
            <p className="text-sm text-arc-muted mb-4">
              If you're holding ETH long-term, restaking can add 3-7% APY without
              locking up your position
            </p>
            <AffiliateCard link={etherfi} medium="networth-tab" />
          </div>
        );
      })()}

      {/* Apex CTA — Net Worth users with significant balance are highly
          qualified leads. Show the strategy call when net worth is meaningful. */}
      {autoNetWorth >= 100000 && (
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <p className="eyebrow mb-1">You've built a real foundation</p>
          <p className="text-sm text-arc-muted mb-4">
            Six figures + is the threshold where strategy starts to matter more
            than savings rate. Talk to an ARC strategist about how to deploy this.
          </p>
          <AffiliateCard
            link={ARC_PRODUCTS.apexone}
            medium="networth-tab-apex"
            featured
          />
        </div>
      )}
    </div>
  );
};

export default NetWorthTab;